import { useEffect, useRef, useState } from "react";
import {
  Check,
  ImagePlus,
  LoaderCircle,
  Save,
  Trash2,
} from "lucide-react";
import { PageHeader } from "./PageHeader.jsx";

const blankSignature = {
  imageDataUrl: "",
  imageWidth: null,
  imageHeight: null,
  displayName: "",
  role: "",
  registration: "",
  alignment: "left",
};

function normalizeProfile(data = {}) {
  return {
    fullName: data.fullName || "",
    email: data.email || "",
    phoneE164: data.phoneE164 || "",
    whatsappE164: data.whatsappE164 || "",
    consents: {
      email: { enabled: data.consents?.email?.enabled ?? true },
      sms: { enabled: data.consents?.sms?.enabled ?? false },
      whatsapp: { enabled: data.consents?.whatsapp?.enabled ?? false },
    },
    legalSignature: {
      ...blankSignature,
      ...data.legalSignature,
    },
  };
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("No pudimos leer esa imagen."));
    };
    image.src = url;
  });
}

async function prepareSignatureImage(file) {
  if (!/^image\/(?:png|jpeg)$/.test(file.type)) {
    throw new Error("Elegí una imagen PNG o JPG.");
  }
  const image = await loadImage(file);
  const maxWidth = 640;
  const maxHeight = 220;
  let scale = Math.min(1, maxWidth / image.naturalWidth, maxHeight / image.naturalHeight);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  let dataUrl = "";
  do {
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    dataUrl = canvas.toDataURL("image/png");
    scale *= 0.8;
  } while (dataUrl.length > 700_000 && canvas.width > 220);
  if (dataUrl.length > 700_000) {
    throw new Error("La imagen es demasiado pesada. Probá con una firma más recortada.");
  }
  return {
    imageDataUrl: dataUrl,
    imageWidth: canvas.width,
    imageHeight: canvas.height,
  };
}

export function SignatureSettingsPage({ loadProfile, saveSignature }) {
  const inputRef = useRef(null);
  const [profile, setProfile] = useState(() => normalizeProfile());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadProfile()
      .then((data) => {
        if (!cancelled) setProfile(normalizeProfile(data));
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadProfile]);

  const updateSignature = (changes) => {
    setProfile((current) => ({
      ...current,
      legalSignature: { ...current.legalSignature, ...changes },
    }));
    setSaved(false);
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const updated = await saveSignature(profile.legalSignature);
      setProfile(normalizeProfile(updated));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2400);
    } catch (requestError) {
      setError(requestError.issues?.[0]?.message || requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const signature = profile.legalSignature;
  return (
    <form className="suite-signature-settings" onSubmit={submit}>
      <PageHeader
        eyebrow="Cuenta · Configuración"
        title="Firma para escritos"
        description="Configurala una vez. Se incluirá por defecto en cada escrito y podrás desactivarla cuando quieras."
        actions={(
          <button className="button primary" disabled={loading || saving}>
            {saving
              ? <LoaderCircle className="suite-spin" size={18} />
              : saved ? <Check size={18} /> : <Save size={18} />}
            {saving ? "Guardando…" : saved ? "Guardada" : "Guardar firma"}
          </button>
        )}
      />

      {error && <div className="suite-signature-error" role="alert">{error}</div>}

      <section className="suite-signature-card" aria-busy={loading}>
        {loading ? (
          <div className="suite-signature-loading">
            <LoaderCircle className="suite-spin" size={22} />
            Cargando firma…
          </div>
        ) : (
          <div className="suite-signature-layout">
            <div>
              <div
                className={`suite-signature-preview is-${signature.alignment}`}
                aria-label="Vista previa de la firma"
              >
                {signature.imageDataUrl ? (
                  <img alt="Firma configurada" src={signature.imageDataUrl} />
                ) : (
                  <span className="suite-signature-placeholder">
                    <ImagePlus size={24} />
                    Tu imagen de firma aparecerá acá
                  </span>
                )}
                {signature.displayName && <strong>{signature.displayName}</strong>}
                {signature.role && <span>{signature.role}</span>}
                {signature.registration && <span>{signature.registration}</span>}
              </div>
              <input
                accept="image/png,image/jpeg"
                hidden
                onChange={async (event) => {
                  const [file] = event.target.files || [];
                  event.target.value = "";
                  if (!file) return;
                  setError("");
                  try {
                    updateSignature(await prepareSignatureImage(file));
                  } catch (imageError) {
                    setError(imageError.message);
                  }
                }}
                ref={inputRef}
                type="file"
              />
              <div className="suite-signature-image-actions">
                <button
                  className="button secondary"
                  onClick={() => inputRef.current?.click()}
                  type="button"
                >
                  <ImagePlus size={16} />
                  {signature.imageDataUrl ? "Cambiar imagen" : "Subir firma"}
                </button>
                {signature.imageDataUrl && (
                  <button
                    className="button danger"
                    onClick={() => updateSignature({
                      imageDataUrl: "",
                      imageWidth: null,
                      imageHeight: null,
                    })}
                    type="button"
                  >
                    <Trash2 size={16} />
                    Quitar imagen
                  </button>
                )}
              </div>
              <small className="suite-signature-help">
                PNG o JPG. Para un mejor resultado, usá una imagen recortada y con fondo transparente.
              </small>
            </div>

            <div className="suite-signature-fields">
              <label>
                <span>Nombre bajo la firma</span>
                <input
                  maxLength={120}
                  onChange={(event) => updateSignature({ displayName: event.target.value })}
                  placeholder={profile.fullName || "Nombre y apellido"}
                  value={signature.displayName}
                />
              </label>
              <label>
                <span>Cargo o profesión</span>
                <input
                  maxLength={120}
                  onChange={(event) => updateSignature({ role: event.target.value })}
                  placeholder="Abogado/a"
                  value={signature.role}
                />
              </label>
              <label>
                <span>Matrícula</span>
                <input
                  maxLength={120}
                  onChange={(event) => updateSignature({ registration: event.target.value })}
                  placeholder="Matrícula 0000"
                  value={signature.registration}
                />
              </label>
              <label>
                <span>Alineación</span>
                <select
                  onChange={(event) => updateSignature({ alignment: event.target.value })}
                  value={signature.alignment}
                >
                  <option value="left">Izquierda</option>
                  <option value="center">Centro</option>
                  <option value="right">Derecha</option>
                </select>
              </label>
            </div>
          </div>
        )}
      </section>
    </form>
  );
}
